/**
 * KnowledgeUploadSheet — AC-K-6 (Knowledge-UX Packet B)
 *
 * Reusable slide-in Sheet for uploading a document or image.
 * Used by:
 *   - AC-K-7: knowledge-item form "Upload Document" button
 *   - AC-K-9: persona Documents tab "Add Document" button
 *
 * On a successful upload the `onUploaded` callback receives the attachment
 * metadata. The calling context decides what to do with it (attach to item,
 * create knowledge item + pin, etc.).
 *
 * Uses the existing AttachmentsService upload flow (presign → R2 PUT → finalize).
 * The Sheet slides in from the right — NOT a centered modal.
 *
 * data-testid="knowledge-upload-sheet" on the Sheet root (for smoke assertions).
 */

import React, { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetFooter } from '@/components/ui/Sheet/Sheet';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Alert } from '@/components/ui/Alert';
import { t } from '@/lib/i18n';
import { AttachmentsService } from '@/services/AttachmentsService';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';

const attachmentsService = new AttachmentsService();

export interface UploadedAttachment {
  id: string;
  title: string;
  filename: string;
  url?: string;
  description?: string;
  itemType?: string;
}

export interface KnowledgeUploadSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful upload. Caller decides what to do with the attachment. */
  onUploaded: (attachment: UploadedAttachment) => void;
  /** Optional title override; defaults to "Upload Document" */
  title?: string;
  /** Optional subtitle shown below the sheet header title */
  subtitle?: string;
}

/** Knowledge item type options per AC-25 + AC-21 enum. */
const KNOWLEDGE_ITEM_TYPES = [
  { value: 'document', label: 'Document' },
  { value: 'fact', label: 'Fact' },
  { value: 'template', label: 'Template' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'standard', label: 'Standard' },
  { value: 'decision_log', label: 'Decision Log' },
  { value: 'method_library', label: 'Method Library' },
  { value: 'advisory_board', label: 'Advisory Board' },
  { value: 'source_of_truth', label: 'Source of Truth' },
];

/**
 * AC-K-6: Reusable upload sheet used in AC-K-7 (knowledge form) and AC-K-9 (persona Documents tab).
 * Slides in from the right (Sheet component, NOT a centered modal).
 */
export function KnowledgeUploadSheet({
  isOpen,
  onClose,
  onUploaded,
  title,
  subtitle,
}: KnowledgeUploadSheetProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [customTitle, setCustomTitle] = useState('');
  const [description, setDescription] = useState('');
  const [itemType, setItemType] = useState('document');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setError(null);
    if (file && !customTitle) {
      // Pre-fill title from filename (strip extension)
      setCustomTitle(file.name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setCustomTitle('');
    setDescription('');
    setItemType('document');
    setError(null);
    onClose();
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError(t('Please select a file to upload'));
      return;
    }
    setIsUploading(true);
    setError(null);
    try {
      const attachment = await attachmentsService.upload(selectedFile, {
        title: customTitle.trim() || selectedFile.name.replace(/\.[^.]+$/, ''),
      });
      onUploaded({
        id: attachment.id,
        title: attachment.title ?? selectedFile.name,
        filename: attachment.originalName ?? selectedFile.name,
        url: attachment.url,
        description: description.trim() || undefined,
        itemType: itemType || 'document',
      });
      handleClose();
    } catch (err: any) {
      setError(err?.message || t('Upload failed — please try again'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Sheet open={isOpen} onClose={handleClose} data-testid="knowledge-upload-sheet">
      <SheetContent className="sm:max-w-md">
        <SheetHeader
          title={title ?? t('Upload Document')}
          subtitle={subtitle}
        />
        <SheetBody>
          <div className="space-y-4">
            {error && (
              <Alert variant="error" data-testid="knowledge-upload-error">
                {error}
              </Alert>
            )}

            {/* File picker — accepts documents and images (AC-K-6) */}
            <div>
              <label className="text-sm font-medium text-foreground block mb-1.5">
                {t('File')} <span className="text-destructive">*</span>
              </label>
              <div
                className="border-2 border-dashed border-border rounded-md p-6 text-center cursor-pointer hover:border-primary-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="knowledge-upload-dropzone"
              >
                <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                {selectedFile ? (
                  <p className="text-sm font-medium text-foreground">{selectedFile.name}</p>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">{t('Click to select a document or image')}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PDF, DOCX, TXT, PNG, JPG, GIF, WEBP
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.gif,.webp"
                  className="hidden"
                  onChange={handleFileChange}
                  data-testid="knowledge-upload-file-input"
                />
              </div>
            </div>

            {/* Document title — F-2 (AC-K-6): design-system Input (not raw <input>) */}
            <div>
              <Input
                id="knowledge-upload-title"
                label={t('Document title')}
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder={t('Leave empty to use filename')}
                data-testid="knowledge-upload-title-input"
              />
            </div>

            {/* Description — optional free text (AC-25 + AC-21) */}
            <div>
              <Textarea
                id="knowledge-upload-description"
                label={t('Description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('Optional description of this document...')}
                rows={2}
                data-testid="knowledge-upload-description-input"
              />
            </div>

            {/* Type — select from item_type enum (AC-25 + AC-21) */}
            <div>
              <label htmlFor="knowledge-upload-type" className="text-sm font-medium text-foreground block mb-1.5">
                {t('Type')}
              </label>
              <Select
                id="knowledge-upload-type"
                value={itemType}
                onValueChange={(v) => setItemType(v)}
                options={KNOWLEDGE_ITEM_TYPES}
                data-testid="knowledge-upload-type-select"
              />
            </div>
          </div>
        </SheetBody>
        <SheetFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            data-testid="knowledge-upload-submit"
          >
            {isUploading ? t('Uploading…') : t('Upload')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
